import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import { useAuthStore } from '@/store/auth';
import { useCoworkerStore } from '@/store/coworker';
import { fetchWithAuth } from '@/lib/api/client';
import { createWorkActivityTracker } from '@/lib/mobileWorkActivity';
import { nativeNotificationEnabled } from '@/lib/nativeNotificationSettings';
import { optionalMobileNativeModule, optionalNativeEvent } from '@/lib/mobileNativeCapabilities';

// Activity surfaces are optional. An unavailable OS capability or rejected
// background update must not interrupt the task/chat UI or leave a rejection.
const bestEffort = operation => {
  try { Promise.resolve(operation()).catch(() => {}); } catch {}
};

export default function MobileWorkActivityHost() {
  const userId = useAuthStore(state => state.token && !state._isSigningOut ? state.user?.id : null);
  const generation = useAuthStore(state => state._sessionGeneration);
  const taskCompleted = useAuthStore(state => Platform.OS !== 'android' || nativeNotificationEnabled(state.user?.preferences?.nativeNotifications, 'taskCompleted'));
  const taskAttention = useAuthStore(state => Platform.OS !== 'android' || nativeNotificationEnabled(state.user?.preferences?.nativeNotifications, 'taskAttention'));
  useEffect(() => {
    const native = optionalMobileNativeModule('MobileWorkActivity', ['update', 'configure']);
    if (!native) return;
    // Android progress is a local notification and must honor the same choices
    // as server alerts. iOS Live Activities have their own system controls.
    const enabled = Platform.OS !== 'android' || taskCompleted || taskAttention;
    const accountId = userId && enabled ? String(userId) : '';
    let stopped = false, unavailable = false;
    const preferencesEnabled = () => Platform.OS !== 'android' || ['taskCompleted', 'taskAttention']
      .some(key => nativeNotificationEnabled(useAuthStore.getState().user?.preferences?.nativeNotifications, key));
    const current = () => !stopped && !!accountId && preferencesEnabled() && useAuthStore.getState().token && !useAuthStore.getState()._isSigningOut
      && String(useAuthStore.getState().user?.id || '') === accountId && useAuthStore.getState()._sessionGeneration === generation;
    bestEffort(() => native.configure(accountId));
    const deliver = next => {
      if (!current()) return;
      const muted = Platform.OS === 'android' && (next.phase === 'completed' && !taskCompleted
        || ['attention', 'failed'].includes(next.phase) && !taskAttention);
      bestEffort(() => native.update({ ...next, phase: muted ? 'stopped' : next.phase, accountId }));
    };
    const track = createWorkActivityTracker(deliver);
    const refresh = () => { if (current()) track(useCoworkerStore.getState()); };
    const tasks = useCoworkerStore.subscribe(refresh);
    const auth = useAuthStore.subscribe(() => { if (!current()) bestEffort(() => native.configure('')); });
    const foreground = AppState.addEventListener('change', state => {
      if (state === 'active') refresh();
    });
    const tokenSubscription = Platform.OS === 'ios' ? optionalNativeEvent(native, 'onWorkActivityToken', event => {
      if (!current() || unavailable || event?.accountId !== accountId) return;
      fetchWithAuth('/json/user/work-live-activity', { method: 'POST', body: event, networkRetries: 0,
        isRequestCurrent: current }).catch(error => {
        // Older servers continue to support on-device updates. Never repeatedly
        // register against an endpoint that has not been deployed/configured.
        if ([404, 503].includes(error?.status)) unavailable = true;
      });
    }) : null;
    refresh();
    return () => {
      stopped = true; tasks(); auth(); foreground.remove(); tokenSubscription?.remove();
      bestEffort(() => native.configure(''));
    };
  }, [userId, generation, taskCompleted, taskAttention]);
  return null;
}
